/**
 * Document blocker codes — the union of every reason a customer-facing
 * forward-progress action ("approve & continue", "send to Proof",
 * "attorney grant", etc.) can be rejected. Shared between the API-level
 * mutation guards (see `assert.ts`) and the web `useDocumentBlocker` UI
 * chain so the two sides can't drift on what "this user is allowed to
 * do this right now" means.
 *
 * Codes are listed in priority order: `evaluateBlockers` walks them in
 * the order declared here. The action-policy map below (`ACTION_BLOCKER_POLICY`)
 * filters which subset applies to each mutation.
 */
export const DOCUMENT_BLOCKER_CODES = [
  // Structural setup gaps — surface on every action and every surface.
  "REPRESENTATION_MISSING",
  "NOTARIZATION_COMPLETE",
  "REGENERATION_PROTECTED",

  // Per-side document-prerequisite gaps. Self before partner so the
  // popup chain doesn't pester the user about partner gaps before they
  // close their own.
  "Q_INCOMPLETE_SELF",
  "FD_INCOMPLETE_SELF",
  "Q_INCOMPLETE_PARTNER",
  "FD_INCOMPLETE_PARTNER",
  "ALIGNMENT_HAS_DISPUTES",
  "FD_APPROVALS_SELF_TODO",
  "FD_APPROVALS_PARTNER_TODO",

  // Document lifecycle. Mark/finalize/notarize ordering.
  "NOT_GENERATED",
  "CONSULTATION_APPROVALS_PENDING",
  "DISPLAYED_GENERATION_STALE",
  "ATTORNEYS_NOT_BOOKED",
  "ATTORNEYS_NOT_APPROVED",
  "NOT_REVIEWED",
  "NOT_FINALIZED",
  "SEVEN_DAY_RULE_ACTIVE",

  // Late-stage / one-shot errors.
  "UNFILLED_MENTIONS",
  "NOTARIZATION_EXISTS",
] as const;

export type DocumentBlockerCode = (typeof DOCUMENT_BLOCKER_CODES)[number];

export const DISPLAYED_GENERATION_STALE_MESSAGE =
  "Your document changed since you opened this page. Please review the updated document before continuing.";

/**
 * Mutations that progress the document state forward and therefore need
 * to be gated. Each maps to an entry in `ACTION_BLOCKER_POLICY` listing
 * the codes that block it.
 */
export const DOCUMENT_ACTIONS = [
  // `POST /documents/:id/generate`
  "GENERATE",

  "MARK_REVIEWED",
  // `PATCH /documents/:id { finalizedAt }` — attorney "Modify Finalize Date".
  // Customer flows write `finalizedAt` only via `/approval/grant`.
  "MARK_FINALIZED",
  // `POST /documents/:id/approval/grant` — attorney approves.
  "ATTORNEY_GRANT_APPROVAL",
  // `POST /documents/:id/notarizations/proof`
  "CREATE_NOTARIZATION_PROOF",
  // `POST /documents/:id/notarizations` (in-person upload flow).
  "CREATE_NOTARIZATION_IN_PERSON",
  // Underlying `generatePdfForDocument` call — any caller (download,
  // attorney grant, proof creation) inherits this gate via the file
  // generation wrapper.
  "GENERATE_PDF",
] as const;

export type DocumentAction = (typeof DOCUMENT_ACTIONS)[number];

export type CreateNotarizationAction = Extract<
  DocumentAction,
  "CREATE_NOTARIZATION_PROOF" | "CREATE_NOTARIZATION_IN_PERSON"
>;

export function isCreateNotarizationAction(
  action: DocumentAction,
): action is CreateNotarizationAction {
  return (
    action === "CREATE_NOTARIZATION_PROOF" ||
    action === "CREATE_NOTARIZATION_IN_PERSON"
  );
}

/**
 * Per-action gate set. A code that is NOT in the set does not block
 * that action, even if it would otherwise apply.
 *
 * Rationale per action:
 *
 * - `GENERATE`: widens the prior `getFDStatus`-only check in
 *   `_generateRoute` to also cover Q + alignment + FD-approvals,
 *   mirroring the client `useDocumentBlocker` chain on `/documents`.
 *   Does NOT include `NOT_GENERATED` (obviously). DOES include
 *   `NOTARIZATION_EXISTS` and `NOTARIZATION_COMPLETE` — regenerating
 *   the PDF after notarization has started would invalidate the doc
 *   the Proof transaction was opened against.
 * - `MARK_REVIEWED`: same set as `GENERATE` plus `NOT_GENERATED` and
 *   `DISPLAYED_GENERATION_STALE`. The latter is request-relative: it is
 *   evaluated only when the caller supplies the generation it reviewed.
 * - `MARK_FINALIZED`: attorneys control the final document, so only
 *   document lifecycle and notarization state apply.
 * - `ATTORNEY_GRANT_APPROVAL`: customer questionnaire, alignment, and
 *   financial state do not override counsel. The document must exist,
 *   editor mentions must be filled, and notarization must not have begun.
 * - `CREATE_NOTARIZATION_*`: every pre-notarization prerequisite,
 *   including `NOTARIZATION_EXISTS`. Represented documents use the contextual
 *   policy below, which omits customer questionnaire, alignment, and financial
 *   prerequisites because counsel owns the approved language.
 * - `GENERATE_PDF`: matches `_generateRoute`'s historical contract
 *   exactly, so the gate can be pushed down into
 *   `generatePdfForDocument` without changing the customer-visible
 *   `/generate` response shape.
 *
 * `NOTARIZATION_EXISTS` semantics: the predicate flags it whenever an
 * active (non-soft-deleted, not-yet-completed) `Notarization` row exists
 * for the document — i.e. the doc is past PoNR. It mirrors
 * `NOTARIZATION_COMPLETE` as a "forward-progress is no longer
 * meaningful" signal and is included in every pre-notarize
 * forward-progress action's gate set. (It was previously scoped only to
 * `CREATE_NOTARIZATION_PROOF` as a Proof-create dedup race backstop;
 * that was a stale-tab bug — see git history for context.)
 */
export const ACTION_BLOCKER_POLICY: Record<
  DocumentAction,
  ReadonlySet<DocumentBlockerCode>
> = {
  GENERATE: new Set<DocumentBlockerCode>([
    "REPRESENTATION_MISSING",
    "NOTARIZATION_COMPLETE",
    "REGENERATION_PROTECTED",
    "Q_INCOMPLETE_SELF",
    "FD_INCOMPLETE_SELF",
    "Q_INCOMPLETE_PARTNER",
    "FD_INCOMPLETE_PARTNER",
    "ALIGNMENT_HAS_DISPUTES",
    "FD_APPROVALS_SELF_TODO",
    "FD_APPROVALS_PARTNER_TODO",
    "NOTARIZATION_EXISTS",
  ]),
  MARK_REVIEWED: new Set<DocumentBlockerCode>([
    "REPRESENTATION_MISSING",
    "NOTARIZATION_COMPLETE",
    "NOT_GENERATED",
    "DISPLAYED_GENERATION_STALE",
    "Q_INCOMPLETE_SELF",
    "FD_INCOMPLETE_SELF",
    "Q_INCOMPLETE_PARTNER",
    "FD_INCOMPLETE_PARTNER",
    "ALIGNMENT_HAS_DISPUTES",
    "FD_APPROVALS_SELF_TODO",
    "FD_APPROVALS_PARTNER_TODO",
    "CONSULTATION_APPROVALS_PENDING",
    "NOTARIZATION_EXISTS",
  ]),
  MARK_FINALIZED: new Set<DocumentBlockerCode>([
    "NOTARIZATION_COMPLETE",
    "NOT_GENERATED",
    "NOTARIZATION_EXISTS",
  ]),
  ATTORNEY_GRANT_APPROVAL: new Set<DocumentBlockerCode>([
    "NOTARIZATION_COMPLETE",
    "NOT_GENERATED",
    "UNFILLED_MENTIONS",
    "NOTARIZATION_EXISTS",
  ]),
  CREATE_NOTARIZATION_PROOF: new Set<DocumentBlockerCode>([
    "REPRESENTATION_MISSING",
    "NOTARIZATION_COMPLETE",
    "NOT_GENERATED",
    "Q_INCOMPLETE_SELF",
    "FD_INCOMPLETE_SELF",
    "Q_INCOMPLETE_PARTNER",
    "FD_INCOMPLETE_PARTNER",
    "ALIGNMENT_HAS_DISPUTES",
    "FD_APPROVALS_SELF_TODO",
    "FD_APPROVALS_PARTNER_TODO",
    "ATTORNEYS_NOT_BOOKED",
    "ATTORNEYS_NOT_APPROVED",
    "NOT_REVIEWED",
    "NOT_FINALIZED",
    "SEVEN_DAY_RULE_ACTIVE",
    "NOTARIZATION_EXISTS",
  ]),
  CREATE_NOTARIZATION_IN_PERSON: new Set<DocumentBlockerCode>([
    "REPRESENTATION_MISSING",
    "NOTARIZATION_COMPLETE",
    "NOT_GENERATED",
    "Q_INCOMPLETE_SELF",
    "FD_INCOMPLETE_SELF",
    "Q_INCOMPLETE_PARTNER",
    "FD_INCOMPLETE_PARTNER",
    "ALIGNMENT_HAS_DISPUTES",
    "FD_APPROVALS_SELF_TODO",
    "FD_APPROVALS_PARTNER_TODO",
    "ATTORNEYS_NOT_BOOKED",
    "ATTORNEYS_NOT_APPROVED",
    "NOT_REVIEWED",
    "NOT_FINALIZED",
    "SEVEN_DAY_RULE_ACTIVE",
    "NOTARIZATION_EXISTS",
  ]),

  GENERATE_PDF: new Set<DocumentBlockerCode>([
    "FD_INCOMPLETE_SELF",
    "FD_INCOMPLETE_PARTNER",
  ]),
};

export const REPRESENTED_NOTARIZATION_BLOCKER_POLICY =
  new Set<DocumentBlockerCode>([
    "REPRESENTATION_MISSING",
    "NOTARIZATION_COMPLETE",
    "NOT_GENERATED",
    "ATTORNEYS_NOT_BOOKED",
    "ATTORNEYS_NOT_APPROVED",
    "NOT_REVIEWED",
    "NOT_FINALIZED",
    "SEVEN_DAY_RULE_ACTIVE",
    "NOTARIZATION_EXISTS",
  ]);

const NOTARIZATION_DATA_REQUIREMENTS: Record<
  CreateNotarizationAction,
  ReadonlySet<DocumentBlockerCode>
> = {
  CREATE_NOTARIZATION_PROOF: new Set([
    ...ACTION_BLOCKER_POLICY.CREATE_NOTARIZATION_PROOF,
    ...REPRESENTED_NOTARIZATION_BLOCKER_POLICY,
  ]),
  CREATE_NOTARIZATION_IN_PERSON: new Set([
    ...ACTION_BLOCKER_POLICY.CREATE_NOTARIZATION_IN_PERSON,
    ...REPRESENTED_NOTARIZATION_BLOCKER_POLICY,
  ]),
};

export function blockerDataRequirementsForAction(
  action: DocumentAction,
): ReadonlySet<DocumentBlockerCode> {
  return isCreateNotarizationAction(action)
    ? NOTARIZATION_DATA_REQUIREMENTS[action]
    : ACTION_BLOCKER_POLICY[action];
}

/**
 * HTTP status to use when surfacing a blocker as a mutation rejection.
 * Pinned to 409 across the board: every code is a "your state conflicts
 * with the action you just tried" condition, which the client treats as
 * "stale tab, refetch and show blocker". `UNFILLED_MENTIONS` is 409 to
 * preserve the existing `ApprovalGrantUnfilledMentionsResponse` status
 * that the editor depends on.
 */
export const BLOCKER_HTTP_STATUS: Record<DocumentBlockerCode, 409> = {
  REPRESENTATION_MISSING: 409,
  NOTARIZATION_COMPLETE: 409,
  REGENERATION_PROTECTED: 409,
  NOT_GENERATED: 409,
  CONSULTATION_APPROVALS_PENDING: 409,
  DISPLAYED_GENERATION_STALE: 409,
  Q_INCOMPLETE_SELF: 409,
  FD_INCOMPLETE_SELF: 409,
  Q_INCOMPLETE_PARTNER: 409,
  FD_INCOMPLETE_PARTNER: 409,
  ALIGNMENT_HAS_DISPUTES: 409,
  FD_APPROVALS_SELF_TODO: 409,
  FD_APPROVALS_PARTNER_TODO: 409,
  ATTORNEYS_NOT_BOOKED: 409,
  ATTORNEYS_NOT_APPROVED: 409,
  NOT_REVIEWED: 409,
  NOT_FINALIZED: 409,
  SEVEN_DAY_RULE_ACTIVE: 409,
  UNFILLED_MENTIONS: 409,
  NOTARIZATION_EXISTS: 409,
};
